import type {
  Activity,
  ActivityDeleteImpact,
  CreateActivityInput,
  CreateGapInput,
  CreateProcessAsIsInput,
  CreateSolutionInput,
  Gap,
  GapDeleteImpact,
  GapDeleteImpactSolution,
  GapDeletePayload,
  ListGapsAggregatedQuery,
  ListGapsAggregatedResponse,
  ProcessAsIs,
  ProcessAsIsDeleteImpact,
  ProcessAsIsDeletePayload,
  ProcessAsIsSummary,
  ProcessEditLog,
  ReorderProcessesInput,
  SetGapActivitiesInput,
  SetSolutionGapsInput,
  Solution,
  UpdateActivityInput,
  UpdateGapInput,
  UpdateProcessAsIsInput,
  UpdateSolutionInput,
} from '@internal/orquestra-contract';
import { OrquestraFinancialClient } from './OrquestraFinancialClient';
import {
  GapDeleteConflictError,
  OrquestraRequestError,
} from './OrquestraApi';

function base(projectId: number): string {
  return `/projects/${projectId}/process-as-is`;
}

/**
 * Serializa `ListGapsAggregatedQuery` em uma query-string estável. Arrays
 * (`criticality`, `category`) são serializados como
 * `?criticality[]=critica&criticality[]=alta`, conforme aceito pelo endpoint
 * agregado (`GET /projects/:projectId/gaps`).
 */
function buildGapsAggregatedQuery(query: ListGapsAggregatedQuery): string {
  const params = new URLSearchParams();
  if (query.processId !== undefined) {
    params.append('processId', String(query.processId));
  }
  if (query.criticality) {
    for (const value of query.criticality) {
      params.append('criticality[]', value);
    }
  }
  if (query.category) {
    for (const value of query.category) {
      params.append('category[]', value);
    }
  }
  if (query.search !== undefined && query.search !== '') {
    params.append('search', query.search);
  }
  if (query.page !== undefined) params.append('page', String(query.page));
  if (query.pageSize !== undefined) {
    params.append('pageSize', String(query.pageSize));
  }
  if (query.sort) params.append('sort', query.sort);
  if (query.sortDir) params.append('sortDir', query.sortDir);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

/**
 * Estende `OrquestraFinancialClient` com os métodos do Process AS-IS (Fase 4.1).
 * Classe intermediária da cadeia de herança — o client concreto que implementa o
 * `OrquestraApi` completo é o `OrquestraImagineClient` (mais derivado).
 */
export class OrquestraProcessAsIsClient extends OrquestraFinancialClient {
  async listProcessesAsIs(projectId: number): Promise<ProcessAsIs[]> {
    return this.request<ProcessAsIs[]>(base(projectId));
  }

  async getProcessAsIsSummary(projectId: number): Promise<ProcessAsIsSummary> {
    return this.request<ProcessAsIsSummary>(`${base(projectId)}/summary`);
  }

  async reorderProcessesAsIs(
    projectId: number,
    input: ReorderProcessesInput,
  ): Promise<ProcessAsIs[]> {
    return this.request<ProcessAsIs[]>(
      `${base(projectId)}/reorder`,
      this.jsonInit('PATCH', input),
    );
  }

  async getProcessAsIs(
    projectId: number,
    processId: number,
  ): Promise<ProcessAsIs> {
    return this.request<ProcessAsIs>(`${base(projectId)}/${processId}`);
  }

  async createProcessAsIs(
    projectId: number,
    input: CreateProcessAsIsInput,
  ): Promise<ProcessAsIs> {
    return this.request<ProcessAsIs>(
      base(projectId),
      this.jsonInit('POST', input),
    );
  }

  async updateProcessAsIs(
    projectId: number,
    processId: number,
    input: UpdateProcessAsIsInput,
  ): Promise<ProcessAsIs> {
    return this.request<ProcessAsIs>(
      `${base(projectId)}/${processId}`,
      this.jsonInit('PUT', input),
    );
  }

  async deleteProcessAsIs(
    projectId: number,
    processId: number,
    payload: ProcessAsIsDeletePayload,
  ): Promise<void> {
    await this.request(
      `${base(projectId)}/${processId}`,
      this.jsonInit('DELETE', payload),
    );
  }

  async getProcessAsIsDeleteImpact(
    projectId: number,
    processId: number,
  ): Promise<ProcessAsIsDeleteImpact> {
    return this.request<ProcessAsIsDeleteImpact>(
      `${base(projectId)}/${processId}/delete-impact`,
    );
  }

  async listActivities(
    projectId: number,
    processId: number,
  ): Promise<Activity[]> {
    return this.request<Activity[]>(
      `${base(projectId)}/${processId}/activities`,
    );
  }

  async createActivity(
    projectId: number,
    processId: number,
    input: CreateActivityInput,
  ): Promise<Activity> {
    return this.request<Activity>(
      `${base(projectId)}/${processId}/activities`,
      this.jsonInit('POST', input),
    );
  }

  async updateActivity(
    projectId: number,
    processId: number,
    activityId: number,
    input: UpdateActivityInput,
  ): Promise<Activity> {
    return this.request<Activity>(
      `${base(projectId)}/${processId}/activities/${activityId}`,
      this.jsonInit('PUT', input),
    );
  }

  async deleteActivity(
    projectId: number,
    processId: number,
    activityId: number,
  ): Promise<void> {
    await this.request(
      `${base(projectId)}/${processId}/activities/${activityId}`,
      this.jsonInit('DELETE'),
    );
  }

  async getActivityDeleteImpact(
    projectId: number,
    processId: number,
    activityId: number,
  ): Promise<ActivityDeleteImpact> {
    return this.request<ActivityDeleteImpact>(
      `${base(projectId)}/${processId}/activities/${activityId}/delete-impact`,
    );
  }

  async listGaps(projectId: number, processId: number): Promise<Gap[]> {
    return this.request<Gap[]>(`${base(projectId)}/${processId}/gaps`);
  }

  async listGapsAggregated(
    projectId: number,
    query: ListGapsAggregatedQuery,
  ): Promise<ListGapsAggregatedResponse> {
    return this.request<ListGapsAggregatedResponse>(
      `/projects/${projectId}/gaps${buildGapsAggregatedQuery(query)}`,
    );
  }

  async downloadGapsCsv(
    projectId: number,
    query: ListGapsAggregatedQuery,
  ): Promise<Blob> {
    const baseUrl = await this.getBaseUrl();
    const path = `/projects/${projectId}/gaps/export${buildGapsAggregatedQuery(
      query,
    )}`;
    const response = await this.fetchApi.fetch(`${baseUrl}${path}`, {
      headers: { Accept: 'text/csv' },
    });
    if (!response.ok) {
      let detail = '';
      try {
        detail = (await response.json())?.error ?? '';
      } catch {
        // corpo sem JSON — mantém detalhe vazio
      }
      throw new OrquestraRequestError(
        detail || `Falha ao exportar gaps (${response.status}).`,
        response.status,
      );
    }
    return response.blob();
  }

  async createGap(
    projectId: number,
    processId: number,
    input: CreateGapInput,
  ): Promise<Gap> {
    return this.request<Gap>(
      `${base(projectId)}/${processId}/gaps`,
      this.jsonInit('POST', input),
    );
  }

  async updateGap(
    projectId: number,
    processId: number,
    gapId: number,
    input: UpdateGapInput,
  ): Promise<Gap> {
    return this.request<Gap>(
      `${base(projectId)}/${processId}/gaps/${gapId}`,
      this.jsonInit('PUT', input),
    );
  }

  async deleteGap(
    projectId: number,
    processId: number,
    gapId: number,
    payload: GapDeletePayload,
  ): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const path = `${base(projectId)}/${processId}/gaps/${gapId}`;
    const response = await this.fetchApi.fetch(
      `${baseUrl}${path}`,
      this.jsonInit('DELETE', payload),
    );
    if (response.ok) return;
    if (response.status === 409) {
      let body: unknown = null;
      try {
        body = await response.json();
      } catch {
        // corpo sem JSON — mantém body vazio
      }
      const error = (body as { error?: { message?: string; details?: unknown } } | null)
        ?.error;
      const details = (error?.details ?? {}) as {
        solutions?: GapDeleteImpactSolution[];
      };
      throw new GapDeleteConflictError(
        error?.message ||
          'Não é possível excluir: existem Soluções que ficariam órfãs.',
        Array.isArray(details.solutions) ? details.solutions : [],
      );
    }
    let detail = '';
    try {
      detail = ((await response.json()) as { error?: string })?.error ?? '';
    } catch {
      // corpo sem JSON — mantém detalhe vazio
    }
    throw new OrquestraRequestError(
      detail || `Requisição ao Orquestra falhou: ${path} (${response.status})`,
      response.status,
    );
  }

  async getGapDeleteImpact(
    projectId: number,
    processId: number,
    gapId: number,
  ): Promise<GapDeleteImpact | null> {
    const baseUrl = await this.getBaseUrl();
    const path = `${base(projectId)}/${processId}/gaps/${gapId}/delete-impact`;
    const response = await this.fetchApi.fetch(`${baseUrl}${path}`);
    if (response.ok) {
      return (await response.json()) as GapDeleteImpact;
    }
    if (response.status === 404) {
      return null;
    }
    let detail = '';
    try {
      detail = ((await response.json()) as { error?: string })?.error ?? '';
    } catch {
      // corpo sem JSON — mantém detalhe vazio
    }
    throw new OrquestraRequestError(
      detail || `Requisição ao Orquestra falhou: ${path} (${response.status})`,
      response.status,
    );
  }

  async setGapActivities(
    projectId: number,
    processId: number,
    gapId: number,
    input: SetGapActivitiesInput,
  ): Promise<{ activityIds: number[] }> {
    return this.request(
      `${base(projectId)}/${processId}/gaps/${gapId}/activities`,
      this.jsonInit('PUT', input),
    );
  }

  async getGapActivities(
    projectId: number,
    processId: number,
    gapId: number,
  ): Promise<number[]> {
    return this.request<number[]>(
      `${base(projectId)}/${processId}/gaps/${gapId}/activities`,
    );
  }

  async listSolutions(
    projectId: number,
    processId: number,
  ): Promise<Solution[]> {
    return this.request<Solution[]>(
      `${base(projectId)}/${processId}/solutions`,
    );
  }

  async createSolution(
    projectId: number,
    processId: number,
    input: CreateSolutionInput,
  ): Promise<Solution> {
    return this.request<Solution>(
      `${base(projectId)}/${processId}/solutions`,
      this.jsonInit('POST', input),
    );
  }

  async updateSolution(
    projectId: number,
    processId: number,
    solutionId: number,
    input: UpdateSolutionInput,
  ): Promise<Solution> {
    return this.request<Solution>(
      `${base(projectId)}/${processId}/solutions/${solutionId}`,
      this.jsonInit('PUT', input),
    );
  }

  async deleteSolution(
    projectId: number,
    processId: number,
    solutionId: number,
  ): Promise<void> {
    await this.request(
      `${base(projectId)}/${processId}/solutions/${solutionId}`,
      this.jsonInit('DELETE'),
    );
  }

  async setSolutionGaps(
    projectId: number,
    processId: number,
    solutionId: number,
    input: SetSolutionGapsInput,
  ): Promise<{ gapIds: number[] }> {
    return this.request(
      `${base(projectId)}/${processId}/solutions/${solutionId}/gaps`,
      this.jsonInit('PUT', input),
    );
  }

  async getSolutionGaps(
    projectId: number,
    processId: number,
    solutionId: number,
  ): Promise<number[]> {
    return this.request<number[]>(
      `${base(projectId)}/${processId}/solutions/${solutionId}/gaps`,
    );
  }

  async getProcessEditLog(
    projectId: number,
    processId: number,
  ): Promise<ProcessEditLog[]> {
    return this.request<ProcessEditLog[]>(
      `${base(projectId)}/${processId}/edit-log`,
    );
  }

  async getActivityEditLog(
    projectId: number,
    processId: number,
    activityId: number,
  ): Promise<ProcessEditLog[]> {
    return this.request<ProcessEditLog[]>(
      `${base(projectId)}/${processId}/activities/${activityId}/edit-log`,
    );
  }

  async getGapEditLog(
    projectId: number,
    processId: number,
    gapId: number,
  ): Promise<ProcessEditLog[]> {
    return this.request<ProcessEditLog[]>(
      `${base(projectId)}/${processId}/gaps/${gapId}/edit-log`,
    );
  }

  async getSolutionEditLog(
    projectId: number,
    processId: number,
    solutionId: number,
  ): Promise<ProcessEditLog[]> {
    return this.request<ProcessEditLog[]>(
      `${base(projectId)}/${processId}/solutions/${solutionId}/edit-log`,
    );
  }
}
